import React from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, IconButton, Menu, MenuItem } from "@mui/material";
import { useDispatch } from "react-redux";
import "./landing.css";
import { AppDispatch, persistor } from "../../redux/store";
import { clearAuthSession, getAccessToken, getRefreshToken, isTokenExpired } from "../../utils/auth";
import { reset as resetGraph } from "../../redux/features/graph/graph.slice";
import { resetStates } from "../../redux/features/appState/appState.slice";
import { resetNodesState } from "../../redux/features/nodes";
import { setSelectedTemplate } from "../template/template.slice";
import { setClickedFalse } from "../../redux/features/popUp/popUp.slice";

// Importing Assets
const StandardLogo = "/gplan logo.svg";
const CubeImage1 = "/assets/landing/cube1.png";
const CubeImage2 = "/assets/landing/cube2.png";
const CubeImage3 = "/assets/landing/cube3.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const isLoggedIn =
    (Boolean(accessToken) && !isTokenExpired(accessToken as string)) ||
    Boolean(refreshToken);

  const handleGraphPlansClick = () => {
    dispatch(setClickedFalse());
    dispatch(resetNodesState());
    dispatch(setSelectedTemplate("blank"));
    navigate("/inputeditor");
  };

  const handleFloorPlansClick = () => {
    sessionStorage.removeItem("newInputEditorState");
    navigate("/newinputeditor");
  };

  const handleHousePlansClick = () => {
    dispatch(setClickedFalse());
    dispatch(resetNodesState());
    dispatch(setSelectedTemplate("housing"));
    navigate("/inputeditor", { state: { editorType: "housing" } });
  };

  const handleSavedDocumentsClick = () => {
    navigate("/saved-documents");
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    dispatch(resetGraph());
    dispatch(resetStates());
    sessionStorage.clear();
    persistor.purge();
    clearAuthSession();
    navigate("/");
  };

  return (
    <div className="landing-container">
      {/* Background Decorations */}
      <img src={CubeImage1} alt="" className="cube-bg cube-1" />
      <img src={CubeImage2} alt="" className="cube-bg cube-2" />
      <img src={CubeImage3} alt="" className="cube-bg cube-3" />

      {/* Header with Transparent Box */}
      <header className="landing-header">
        <div className="landing-logo-container">
          <img src={StandardLogo} alt="GPLAN" className="landing-logo-standard" />
        </div>
        {isLoggedIn ? (
          <>
            <IconButton
              onClick={handleProfileMenuOpen}
              sx={{
                backgroundColor: "#FBE5C6",
                border: "2px solid #2b2b2b",
                borderRadius: "10px",
                px: 1.5,
                py: 0.5,
                color: "#111",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "#FF9A56",
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                {String(localStorage.getItem("name") || "U").charAt(0).toUpperCase()}
              </Avatar>
              <span style={{ fontSize: "14px", fontWeight: 600 }}>Profile</span>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </>
        ) : (
          <button className="login-btn" onClick={handleLogin}>
            <img src="https://api.iconify.design/ph:user-circle.svg" alt="User" width="20" />
            Sign up / Login
          </button>
        )}
      </header>

      {/* Left Section */}
      <section className="landing-left">
        <div className="landing-content">
          <p className="landing-tagline">A GENERATIVE FLOOR PLAN DESIGNER</p>
          <h1 className="landing-title">
            The Future of Early-Stage Floor Plan Design
          </h1>
          <p className="landing-subtitle">
            Born in the desert sands of BITS Pilani, India. GPLAN is the brainchild of Prof. Krishnendra Shekhawat, an esteemed mathematics professor, and his students.
          </p>
          <button className="find-more-btn" onClick={() => window.open('https://gplan.in', '_blank')}>
            Find out more
          </button>
        </div>
      </section>

      {/* Wavy Divider */}
      <div className="landing-divider">
        <svg viewBox="0 0 1440 1024" preserveAspectRatio="none" className="wavy-svg">
          <path
            className="wavy-path"
            d="M720 0 
               C 650 250, 850 750, 720 1024 
               L 1440 1024 
               L 1440 0 
               Z"
          />
        </svg>
      </div>

      {/* Right Section */}
      <section className="landing-right">
        <div className="cards-container">
          {/* Box 1 */}
          <div className="input-card" onClick={handleGraphPlansClick}>
            <div className="card-header">
              <span className="card-title">Graph-Plans</span>
              <span className="arrow-icon">→</span>
            </div>
            <p className="card-description">
              Generate multiple floorplan layouts within a specified rectangular plot, based on fixed room dimensions and defined adjacency requirements.
            </p>
          </div>

          {/* Box 2 */}
          <div className="input-card" onClick={handleFloorPlansClick}>
            <div className="card-header">
              <span className="card-title">Floor-plans</span>
              <span className="arrow-icon">→</span>
            </div>
            <p className="card-description">
              Generate multiple floorplan layouts within a specified non-rectangular plot boundary, considering room dimensions and adjacency requirements.
            </p>
          </div>

          {/* Box 3 */}
          <div className="input-card" onClick={handleHousePlansClick}>
            <div className="card-header">
              <span className="card-title">House-Plans</span>
              <span className="arrow-icon">→</span>
            </div>
            <p className="card-description">
              Generate multiple housing floorplans within a defined rectangular plot boundary, where each functional space is enriched with inbuilt imagery.
            </p>
          </div>

          {/* Box 4 - Description only */}
          <div className="input-card" style={{ cursor: 'default', opacity: 0.9 }}>
            <p className="card-description" style={{ fontSize: '14px', fontWeight: 500 }}>
              Each generated floorplan can be easily saved and further customized—allowing you to move rooms and walls, as well as add doors and windows with full flexibility.
            </p>
          </div>
        </div>

        <button className="saved-docs-btn" onClick={handleSavedDocumentsClick}>
          <span>Saved Documents</span>
          <span className="saved-docs-arrow" aria-hidden="true">→</span>
        </button>
      </section>
    </div>
  );
};

export default LandingPage;